// Camera frames -> hardware H.264 (WebCodecs VideoEncoder).
//
// Frame sources:
//  - "mstp": MediaStreamTrackProcessor (Chrome/Edge) — frames straight off the
//    track with capture timestamps.
//  - "rvfc": requestVideoFrameCallback on the (already rendered) preview
//    <video> + `new VideoFrame(videoEl)` (Safari, which lacks MSTP).
//
// Timestamps are re-based to 0 so video and audio (which starts at 0 by
// construction) share a timeline in the muxer. Keyframes are forced every 2s
// of media time. Supports live reconfigure (bitrate/fps, for the adaptive
// controller) and hot track replacement (camera switch while live).

export const VIDEO_TARGET_BITRATE = 2_800_000;
export const VIDEO_FLOOR_BITRATE = 800_000;
export const VIDEO_FPS = 30;
const KEYFRAME_INTERVAL_US = 2_000_000;

const CODEC_CANDIDATES = ["avc1.4D401F", "avc1.42E01F"]; // main, then baseline

export async function pickVideoCodec(width, height) {
  for (const codec of CODEC_CANDIDATES) {
    try {
      const { supported } = await VideoEncoder.isConfigSupported({
        codec,
        width,
        height,
        bitrate: VIDEO_TARGET_BITRATE,
        framerate: VIDEO_FPS,
        avc: { format: "avc" },
      });
      if (supported) return codec;
    } catch {
      // try next candidate
    }
  }
  return null;
}

export function createVideoPipeline({ track, videoEl, framePath, codec, onChunk, onRawFrame, onOrientationChange, onError }) {
  const settings = track.getSettings();
  let width = settings.width || 1280;
  let height = settings.height || 720;
  // rvfc (Safari/iOS): frames come from the rendered element, which applies
  // rotation — trust its dimensions over the sensor's (a portrait phone
  // reports landscape sensor dims but renders 720x1280 upright).
  if (framePath !== "mstp" && videoEl && videoEl.videoWidth) {
    width = videoEl.videoWidth;
    height = videoEl.videoHeight;
  }
  let dimMismatch = 0;
  let running = true;
  // Sources (camera, compositor canvas, replacement tracks) sit on different
  // clock origins; output timestamps must be one continuous monotonic
  // timeline starting at 0 or the muxer throws. Any backwards or huge-forward
  // jump is treated as a source switch and re-based seamlessly.
  let tsOffset = null;
  let lastOutTs = -1;
  const FRAME_GAP_US = Math.round(1e6 / VIDEO_FPS);
  const MAX_JUMP_US = 5_000_000;
  let lastKeyTs = -Infinity;
  let lastEncodedTs = -Infinity;
  let minFrameIntervalUs = 0; // 0 = no fps cap beyond the camera's
  let stopFrameSource = () => {};

  const baseConfig = {
    codec,
    avc: { format: "avc" },
    width,
    height,
    bitrate: VIDEO_TARGET_BITRATE,
    framerate: VIDEO_FPS,
    latencyMode: "realtime",
  };

  const encoder = new VideoEncoder({
    output: (chunk, meta) => onChunk(chunk, meta),
    error: (e) => onError?.(e),
  });
  encoder.configure(baseConfig);

  const encodeFrame = (frame) => {
    if (!running || encoder.state !== "configured") {
      frame.close();
      return;
    }
    // Device rotation changes frame dimensions mid-stream; the encoder can't
    // follow. Drop the odd frame, and after a sustained change ask the engine
    // to restart the publish with the new orientation.
    if (frame.codedWidth !== width || frame.codedHeight !== height) {
      frame.close();
      dimMismatch += 1;
      if (dimMismatch === 20) onOrientationChange?.();
      return;
    }
    dimMismatch = 0;
    if (tsOffset === null) tsOffset = -frame.timestamp;
    let ts = frame.timestamp + tsOffset;
    if (ts <= lastOutTs || ts > lastOutTs + MAX_JUMP_US) {
      tsOffset = lastOutTs + FRAME_GAP_US - frame.timestamp;
      ts = frame.timestamp + tsOffset;
    }
    lastOutTs = ts;
    const rebased = new VideoFrame(frame, { timestamp: ts });
    frame.close();
    try {
      // Local recorder gets EVERY frame — it must not inherit the stream
      // encoder's fps caps or congestion drops.
      onRawFrame?.(rebased);
      // Never let the stream encoder queue grow — drop frames instead.
      if (encoder.encodeQueueSize > 2) return;
      // fps cap from the adaptive controller (slight tolerance for jitter)
      if (minFrameIntervalUs && ts - lastEncodedTs < minFrameIntervalUs * 0.9) return;
      const keyFrame = ts - lastKeyTs >= KEYFRAME_INTERVAL_US;
      if (keyFrame) lastKeyTs = ts;
      lastEncodedTs = ts;
      encoder.encode(rebased, { keyFrame });
    } finally {
      rebased.close();
    }
  };

  const startMstpSource = (srcTrack) => {
    if (srcTrack.readyState === "ended") {
      // Engine renews dead tracks before starting; this is a last-resort
      // guard so a race never surfaces the cryptic MSTP constructor error.
      onError?.(new Error("Camera was interrupted — select it again or reload the page"));
      return;
    }
    const reader = new MediaStreamTrackProcessor({ track: srcTrack }).readable.getReader();
    let active = true;
    (async () => {
      try {
        while (running && active) {
          const { value, done } = await reader.read();
          if (done) break;
          if (!running || !active) {
            value.close();
            break;
          }
          encodeFrame(value);
        }
      } catch (e) {
        if (running && active) onError?.(e);
      }
    })();
    stopFrameSource = () => {
      active = false;
      reader.cancel().catch(() => {});
    };
  };

  if (framePath === "mstp") {
    startMstpSource(track);
  } else {
    // rvfc path: sample the preview element (whatever stream it displays), so
    // camera switches are just a srcObject change. mediaTime is seconds on
    // the capture clock.
    let rafId = null;
    const onFrame = (_now, meta) => {
      if (!running) return;
      try {
        const frame = new VideoFrame(videoEl, { timestamp: Math.round(meta.mediaTime * 1e6) });
        encodeFrame(frame);
      } catch {
        // Element not ready yet (width 0) — skip this frame.
      }
      rafId = videoEl.requestVideoFrameCallback(onFrame);
    };
    rafId = videoEl.requestVideoFrameCallback(onFrame);
    stopFrameSource = () => {
      if (rafId && videoEl.cancelVideoFrameCallback) videoEl.cancelVideoFrameCallback(rafId);
    };
  }

  return {
    width,
    height,

    // Live bitrate/fps change from the adaptive controller. Chrome supports
    // configure() on a running encoder; if it throws we keep the old config.
    reconfigure({ bitrate, fps }) {
      minFrameIntervalUs = fps && fps < VIDEO_FPS ? Math.round(1e6 / fps) : 0;
      try {
        encoder.configure({ ...baseConfig, bitrate, framerate: fps || VIDEO_FPS });
      } catch (e) {
        // keep streaming at the previous settings
      }
    },

    // Camera switch while live (same dimensions). New track shares the same
    // capture clock, so the rebased timeline stays continuous.
    replaceTrack(newTrack) {
      if (framePath === "mstp") {
        stopFrameSource();
        startMstpSource(newTrack);
      }
      lastKeyTs = -Infinity; // force a keyframe at the cut
    },

    async stop() {
      running = false;
      stopFrameSource();
      try {
        if (encoder.state === "configured") await encoder.flush();
      } catch {
        // flush after error is fine to ignore
      }
      try {
        encoder.close();
      } catch {}
    },
  };
}
